import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import campaignService from "../services/campaignService";
import { ReviewTodo } from "../types/newsletterEdition";

const CHECK_LABELS: Record<string, string> = {
  feedback_unresolved: "Unresolved feedback",
  content_requests_pending: "Pending content requests",
  image_alt: "Image alt text",
  toc_alignment: "Table of contents alignment",
  em_dashes: "Em dash count",
  title_keywords: "Title test keywords",
  button_https: "Non-HTTPS button links",
  broken_link: "Broken links & images",
  image_format: "Disallowed image formats (WebP/HEIC)",
  forced_negation: "Forced negations",
  staccato_pairs: "Staccato pairs",
  isocolon_metaphor: "Isocolon metaphor-pairs",
  backward_reference: "Backward-reference endings",
  feedback_not_incorporated: "Feedback not incorporated",
  blueprint_alignment: "Blueprint section alignment",
  typos: "Typos",
  grammar: "Grammar",
  missing_send_date: "Missing send date",
  past_date_in_content: "Past dates in content",
  word_count: "Word count",
  intro_redundancy: "Redundant intro",
  intro_spoiler: "Spoiled intro",
  repeated_content: "Repeated content",
  consistency_unresolved: "Consistency vs previous editions",
  bolding: "Bolding",
  duplicate_link: "Duplicate article links",
  missing_button_text: "Buttons with no label",
};

// Checks the review pipeline does NOT run — their findings are filed by the NL
// verify pipeline (see EXTERNAL_CHECK_IDS in
// newsletter_edition_review_pipeline_service.rb). They get a label so a todo
// reads properly, but they must stay out of the "passed" strip: no todo here
// means that pipeline hasn't run, which is not the same as a clean result.
const EXTERNAL_CHECK_IDS = new Set([
  "repeated_content",
  "consistency_unresolved",
  "bolding",
  "duplicate_link",
  "missing_button_text",
]);

// Checks whose findings come from an LLM — labeled with an "AI" badge since
// they can include false positives (dismissed by checking them off).
const AI_CHECK_IDS = new Set(["forced_negation", "staccato_pairs", "isocolon_metaphor", "backward_reference", "feedback_not_incorporated", "blueprint_alignment", "typos", "grammar", "intro_redundancy", "intro_spoiler"]);

interface ReviewPipelinePanelProps {
  newsletterEditionId: string;
  scheduledSendStartDate?: string | null;
  onScrollToRow: (todo: ReviewTodo) => void;
  onTodosUpdated: (todos: ReviewTodo[]) => void;
  onTemplateChanged?: () => void;
  onClose: () => void;
}

const ReviewPipelinePanel: React.FC<ReviewPipelinePanelProps> = ({
  newsletterEditionId,
  scheduledSendStartDate,
  onScrollToRow,
  onTodosUpdated,
  onTemplateChanged,
}) => {
  // Todos live in their own table on the server — the panel always starts by
  // fetching them, so open in the loading state.
  const [todos, setTodos] = useState<ReviewTodo[]>([]);
  const [isRunning, setIsRunning] = useState(true);
  const [hasRun, setHasRun] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  // check_id being bulk-toggled ("Check all typos") — disables that group's
  // header button while the request is in flight.
  const [bulkResolving, setBulkResolving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Set when the send-time recheck surfaces issues — the editor then chooses to
  // skip them (send anyway) or edit the newsletter first.
  const [pendingItems, setPendingItems] = useState<ReviewTodo[] | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  // "Clear" once a run has produced results and nothing is outstanding — either
  // all checks passed (no todos) or every todo has been checked off.
  const allDone = hasRun && todos.every((t) => t.done);

  const hasFutureSendDate = (() => {
    if (!scheduledSendStartDate) return false;
    return new Date(scheduledSendStartDate) > new Date();
  })();

  const doneCount = todos.filter((t) => t.done).length;
  // Checks that produced no findings at all — shown as a compact "passed" strip.
  const passedCheckIds =
    hasRun && !isRunning
      ? Object.keys(CHECK_LABELS).filter(
          (id) => !EXTERNAL_CHECK_IDS.has(id) && !todos.some((t) => t.check_id === id)
        )
      : [];

  const hasAutoRunRef = useRef(false);
  useEffect(() => {
    if (hasAutoRunRef.current) return;
    hasAutoRunRef.current = true;
    syncFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncFromServer = async () => {
    try {
      const { todos: serverTodos } = await campaignService.getReviewTodos(newsletterEditionId);
      if (serverTodos.length === 0) {
        // No saved todos yet — running the pipeline is the only way they get created.
        runPipeline();
      } else {
        setTodos(serverTodos);
        onTodosUpdated(serverTodos);
        setHasRun(true);
        setIsRunning(false);
      }
    } catch {
      // Don't point users at "Re-run checks" here — the read failed but the
      // saved todos (and their done flags) are intact on the server, and a
      // re-run would recreate everything unchecked. Offer a plain retry.
      setError("Failed to load review items.");
      setIsRunning(false);
    }
  };

  const retryLoad = () => {
    setError(null);
    setIsRunning(true);
    syncFromServer();
  };

  const runPipeline = async () => {
    setIsRunning(true);
    setError(null);
    setSendSuccess(false);
    try {
      const result = await campaignService.reviewPipeline(newsletterEditionId);
      setTodos(result.todos);
      onTodosUpdated(result.todos);
      setHasRun(true);
    } catch (err) {
      setError("Failed to run review pipeline. Please try again.");
    } finally {
      setIsRunning(false);
    }
  };

  const toggleTodo = async (todo: ReviewTodo) => {
    setTodos((current) =>
      current.map((t) => (t.id === todo.id ? { ...t, done: !t.done } : t))
    );
    try {
      const result = await campaignService.updateReviewTodo(
        newsletterEditionId,
        todo.id,
        !todo.done
      );
      setTodos(result.todos);
      onTodosUpdated(result.todos);
    } catch {
      // Revert only this item — a whole-array snapshot would clobber other
      // toggles that resolved while this request was in flight.
      setTodos((current) =>
        current.map((t) => (t.id === todo.id ? { ...t, done: todo.done } : t))
      );
    }
  };

  const handleAutoResolve = async (todo: ReviewTodo) => {
    setResolvingId(todo.id);
    setError(null);
    try {
      const result = await campaignService.autoResolveTodo(newsletterEditionId, todo.id);
      setTodos(result.todos);
      onTodosUpdated(result.todos);
      // Reload the editor so the corrected alt text shows up in the canvas.
      onTemplateChanged?.();
    } catch (err: any) {
      setError(
        err?.response?.data?.error || "Failed to auto-fix the alt text. Please try again."
      );
    } finally {
      setResolvingId(null);
    }
  };

  const handleSendForReview = async () => {
    setIsSending(true);
    setError(null);
    try {
      // Re-run the checks first (dry run — the saved checklist is untouched) so
      // nothing introduced since the last run slips through. Clean → send now;
      // otherwise surface the pending items and let the editor decide.
      const { pending } = await campaignService.reviewRecheck(newsletterEditionId);
      if (pending && pending.length > 0) {
        setPendingItems(pending);
      } else {
        await campaignService.sendForReview(newsletterEditionId);
        setSendSuccess(true);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.error || "Failed to send for review. Please try again."
      );
    } finally {
      setIsSending(false);
    }
  };

  // "Skip" — send despite the pending items. The saved checklist is still
  // all-done, so the server's send gate passes.
  const handleSkipAndSend = async () => {
    setIsSending(true);
    setError(null);
    try {
      await campaignService.sendForReview(newsletterEditionId);
      setPendingItems(null);
      setSendSuccess(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.error || "Failed to send for review. Please try again."
      );
    } finally {
      setIsSending(false);
    }
  };

  // "Edit newsletter first" — override the checklist with the fresh findings
  // (every pending item reopened), then return to the panel without sending.
  const handleEditFirst = async () => {
    setIsApplying(true);
    setError(null);
    try {
      const { todos: applied } = await campaignService.reviewRecheck(newsletterEditionId, true);
      if (applied) {
        setTodos(applied);
        onTodosUpdated(applied);
      }
      setPendingItems(null);
    } catch (err: any) {
      setError(
        err?.response?.data?.error || "Failed to refresh the checklist. Please try again."
      );
    } finally {
      setIsApplying(false);
    }
  };

  const handleNavigate = (todo: ReviewTodo) => {
    if (todo.row_uuid || todo.row_index != null) {
      onScrollToRow(todo);
    }
  };

  // "Check all typos" — flip every todo in one check category to done (or back
  // to not-done when the group is already fully checked). Optimistic update,
  // then the server returns the authoritative list; on failure re-sync.
  const toggleCheckAll = async (checkId: string, items: ReviewTodo[]) => {
    const allDone = items.every((t) => t.done);
    const newDone = !allDone;
    setBulkResolving(checkId);
    setError(null);
    setTodos((current) =>
      current.map((t) => (t.check_id === checkId ? { ...t, done: newDone } : t))
    );
    try {
      const result = await campaignService.bulkUpdateReviewTodos(
        newsletterEditionId,
        checkId,
        newDone
      );
      setTodos(result.todos);
      onTodosUpdated(result.todos);
    } catch (err: any) {
      setError(
        err?.response?.data?.error || "Failed to update review items. Please try again."
      );
      try {
        const { todos: fresh } = await campaignService.getReviewTodos(newsletterEditionId);
        setTodos(fresh);
        onTodosUpdated(fresh);
      } catch {
        // Leave the optimistic state as-is; the next manual toggle re-syncs.
      }
    } finally {
      setBulkResolving(null);
    }
  };

  const groupedByCheck = todos.reduce<Record<string, ReviewTodo[]>>((acc, t) => {
    acc[t.check_id] = acc[t.check_id] || [];
    acc[t.check_id].push(t);
    return acc;
  }, {});

  const groupedPending = (pendingItems || []).reduce<Record<string, ReviewTodo[]>>((acc, t) => {
    (acc[t.check_id] = acc[t.check_id] || []).push(t);
    return acc;
  }, {});

  const linkButtonStyle: React.CSSProperties = {
    fontSize: "0.78em",
    color: "#5D5DED",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textDecoration: "underline",
  };

  return (
    <div
      style={{
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        // Fit exactly inside the SidePanel's scroll wrapper so only the todo
        // list scrolls — without these the padding overflows the wrapper
        // (content-box sizing) and produces a second, outer scrollbar.
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
        <h2 style={{ margin: 0, fontSize: "1.2em", fontWeight: 700 }}>AutoReview</h2>
        <button
          className="tertiary-button"
          onClick={runPipeline}
          disabled={isRunning || resolvingId !== null}
          style={{ fontSize: "0.85em" }}
        >
          {isRunning ? "Running…" : "Re-run checks"}
        </button>
      </div>

      {error && (
        <div
          style={{
            backgroundColor: "#fde8e8",
            color: "#c0392b",
            border: "1px solid #e57373",
            borderRadius: "6px",
            padding: "8px 12px",
            marginBottom: "12px",
            fontSize: "0.9em",
          }}
        >
          {error}
          {!hasRun && !isRunning && (
            <button
              onClick={retryLoad}
              style={{
                marginLeft: "10px",
                color: "#c0392b",
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                textDecoration: "underline",
                fontSize: "1em",
              }}
            >
              Retry
            </button>
          )}
        </div>
      )}

      {sendSuccess && (
        <div
          style={{
            backgroundColor: "#e0f7df",
            color: "#2e7d32",
            border: "1px solid #2e7d32",
            borderRadius: "6px",
            padding: "8px 12px",
            marginBottom: "12px",
            fontSize: "0.9em",
          }}
        >
          ✅ Sent for internal review!
        </div>
      )}

      {isRunning ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            color: "#888",
            marginTop: "40px",
          }}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              border: "3px solid #e0e0e0",
              borderTopColor: "#5D5DED",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <div>Running checks…</div>
          <div style={{ fontSize: "0.8em", color: "#aaa" }}>
            Links, images, feedback and AI checks can take a few seconds
          </div>
        </div>
      ) : !hasRun ? null : todos.length === 0 ? (
        <div
          style={{
            backgroundColor: "#e0f7df",
            color: "#2e7d32",
            border: "1px solid #2e7d32",
            borderRadius: "8px",
            padding: "16px",
            textAlign: "center",
            marginTop: "16px",
          }}
        >
          ✅ All checks passed — no issues found!
        </div>
      ) : (
        <>
          <div style={{ marginBottom: "12px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "0.8em",
                color: "#666",
                marginBottom: "4px",
              }}
            >
              <span>
                {doneCount} of {todos.length} resolved
              </span>
              <span>{Math.round((doneCount / todos.length) * 100)}%</span>
            </div>
            <div
              style={{
                height: "6px",
                backgroundColor: "#ececec",
                borderRadius: "3px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${(doneCount / todos.length) * 100}%`,
                  backgroundColor: "#2e7d32",
                  borderRadius: "3px",
                  transition: "width 0.25s ease",
                }}
              />
            </div>
          </div>

          {passedCheckIds.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                marginBottom: "14px",
              }}
            >
              {passedCheckIds.map((id) => (
                <span
                  key={id}
                  style={{
                    fontSize: "0.72em",
                    color: "#2e7d32",
                    backgroundColor: "#e0f7df",
                    border: "1px solid #b6e2b3",
                    borderRadius: "10px",
                    padding: "2px 8px",
                    whiteSpace: "nowrap",
                  }}
                >
                  ✓ {CHECK_LABELS[id]}
                </span>
              ))}
            </div>
          )}

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {Object.entries(groupedByCheck).map(([checkId, items]) => (
            <div key={checkId} style={{ marginBottom: "20px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "0.8em",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "#888",
                  letterSpacing: "0.05em",
                  marginBottom: "8px",
                }}
              >
                <span>{CHECK_LABELS[checkId] ?? checkId}</span>
                <span style={{ fontWeight: 400, color: "#aaa" }}>
                  {items.filter((t) => t.done).length}/{items.length}
                </span>
                {AI_CHECK_IDS.has(checkId) && (
                  <span
                    title="Found by AI — may include false positives. Check an item off to dismiss it."
                    style={{
                      fontSize: "0.85em",
                      fontWeight: 600,
                      color: "#5D5DED",
                      backgroundColor: "#eeeefc",
                      border: "1px solid #c9c9f5",
                      borderRadius: "8px",
                      padding: "0px 6px",
                      letterSpacing: "normal",
                      cursor: "help",
                    }}
                  >
                    AI
                  </span>
                )}
                {items.length > 1 && (
                  <button
                    onClick={() => toggleCheckAll(checkId, items)}
                    disabled={bulkResolving === checkId}
                    title={
                      items.every((t) => t.done)
                        ? `Mark every ${CHECK_LABELS[checkId] ?? checkId} item as not done`
                        : `Mark every ${CHECK_LABELS[checkId] ?? checkId} item as done`
                    }
                    style={{
                      marginLeft: "auto",
                      fontSize: "0.72em",
                      fontWeight: 600,
                      color: bulkResolving === checkId ? "#aaa" : "#5D5DED",
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: bulkResolving === checkId ? "default" : "pointer",
                      textDecoration: "underline",
                      textTransform: "none",
                      letterSpacing: "normal",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {bulkResolving === checkId
                      ? "…"
                      : items.every((t) => t.done)
                      ? "Uncheck all"
                      : "Check all"}
                  </button>
                )}
              </div>
              {items.map((todo) => {
                const canNavigate = !!todo.row_uuid || todo.row_index != null;
                const canAutoFix = todo.check_id === "image_alt" && !!todo.expected_alt;
                return (
                  <div
                    key={todo.id}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "10px",
                      padding: "10px 12px",
                      borderRadius: "6px",
                      border: "1px solid #e0e0e0",
                      backgroundColor: todo.done ? "#f9fafb" : "#fff",
                      marginBottom: "6px",
                      opacity: todo.done ? 0.6 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={todo.done}
                      onChange={() => toggleTodo(todo)}
                      style={{ marginTop: "2px", cursor: "pointer", flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          fontSize: "0.9em",
                          color: "#333",
                          textDecoration: todo.done ? "line-through" : "none",
                          wordBreak: "break-word",
                        }}
                      >
                        {todo.message}
                      </span>
                      {(canNavigate || canAutoFix) && (
                        <div style={{ display: "flex", gap: "14px", marginTop: "6px", alignItems: "center" }}>
                          {canNavigate && (
                            <button onClick={() => handleNavigate(todo)} style={linkButtonStyle}>
                              Jump to section
                            </button>
                          )}
                          {canAutoFix && (
                            <button
                              onClick={() => handleAutoResolve(todo)}
                              disabled={resolvingId !== null || todo.done}
                              style={{
                                ...linkButtonStyle,
                                color: resolvingId !== null || todo.done ? "#aaa" : "#2e7d32",
                                cursor: resolvingId !== null || todo.done ? "default" : "pointer",
                              }}
                            >
                              {resolvingId === todo.id ? "Fixing…" : "Auto-fix alt text"}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        </>
      )}

      {allDone && !sendSuccess && !isRunning && (
        <div style={{ marginTop: "16px", borderTop: "1px solid #e0e0e0", paddingTop: "16px" }}>
          {!hasFutureSendDate && (
            <div
              style={{
                backgroundColor: "#fff8e1",
                color: "#8d6e00",
                border: "1px solid #ffe082",
                borderRadius: "6px",
                padding: "8px 12px",
                marginBottom: "10px",
                fontSize: "0.85em",
              }}
            >
              {scheduledSendStartDate
                ? "The scheduled send date is in the past. Please update it before sending for review."
                : "A scheduled send date is required before sending for review."}
            </div>
          )}
          <button
            className="primary-button"
            onClick={handleSendForReview}
            disabled={isSending || pendingItems !== null || !hasFutureSendDate}
            style={{ width: "100%" }}
          >
            {isSending ? "Re-checking…" : "Send for Internal Review"}
          </button>
        </div>
      )}

      {/* Portal to body: the side panel animates via CSS transform, which would
          otherwise trap this position:fixed overlay inside the 510px panel. */}
      {pendingItems && createPortal(
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100000,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              padding: "24px",
              width: "min(520px, 92vw)",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            }}
          >
            <h2 style={{ margin: "0 0 8px", fontSize: "1.2em", fontWeight: 700 }}>
              {pendingItems.length} item{pendingItems.length === 1 ? "" : "s"} still pending
            </h2>
            <p style={{ margin: "0 0 16px", fontSize: "0.9em", color: "#666" }}>
              A fresh check found issues that aren't resolved in the current newsletter.
              You can skip them and send for review anyway, or edit the newsletter first
              (this updates the checklist with these items).
            </p>

            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", marginBottom: "16px" }}>
              {Object.entries(groupedPending).map(([checkId, items]) => (
                <div key={checkId} style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      fontSize: "0.78em",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      color: "#888",
                      letterSpacing: "0.05em",
                      marginBottom: "6px",
                    }}
                  >
                    {CHECK_LABELS[checkId] ?? checkId} ({items.length})
                  </div>
                  {items.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        fontSize: "0.88em",
                        color: "#333",
                        padding: "8px 10px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "6px",
                        marginBottom: "6px",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.message}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              <button
                className="primary-button"
                onClick={handleEditFirst}
                disabled={isApplying || isSending}
                style={{ width: "100%" }}
              >
                {isApplying ? "Updating checklist…" : "Edit newsletter first"}
              </button>
              <button
                className="tertiary-button"
                onClick={handleSkipAndSend}
                disabled={isApplying || isSending}
                style={{ width: "100%" }}
              >
                {isSending ? "Sending…" : "Skip & send for review"}
              </button>
              <button
                onClick={() => setPendingItems(null)}
                disabled={isApplying || isSending}
                style={{
                  background: "none",
                  border: "none",
                  color: "#888",
                  cursor: isApplying || isSending ? "default" : "pointer",
                  fontSize: "0.85em",
                  textDecoration: "underline",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ReviewPipelinePanel;
